import { Component, signal, inject, OnInit, DestroyRef, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, NavigationStart, NavigationCancel, NavigationError, Router, RouterOutlet } from '@angular/router';
import { ViewportScroller, isPlatformBrowser } from '@angular/common';
import { NavbarComponent } from './components/navbar/navbar.component';
import { FooterComponent } from './components/footer/footer.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NavbarComponent, FooterComponent],
  template: `
    @if (isNavigating()) {
      <div class="fixed top-0 left-0 h-[3px] bg-blue-500 z-[200] pointer-events-none"
           style="animation: navprogress 1.5s ease-in-out forwards; will-change: width"></div>
    }
    <app-navbar />
    <main class="pt-[70px]">
      <router-outlet />
    </main>
    <app-footer />
  `,
  styles: [`
    @keyframes navprogress {
      0%   { width: 0%; }
      60%  { width: 80%; }
      100% { width: 100%; }
    }
  `],
})
export class App implements OnInit {
  private router = inject(Router);
  private viewportScroller = inject(ViewportScroller);
  private destroyRef = inject(DestroyRef);
  private platformId = inject(PLATFORM_ID);

  isNavigating = signal(false);
  private firstNavDone = false;

  ngOnInit(): void {
    this.injectGtm();
    const sub = this.router.events.subscribe((e) => {
      if (e instanceof NavigationStart) {
        if (this.firstNavDone) {
          this.isNavigating.set(true);
        }
      } else if (e instanceof NavigationEnd) {
        if (!this.firstNavDone) {
          this.firstNavDone = true;
          this.dismissLoadingOverlay();
        } else {
          this.isNavigating.set(false);
        }
        if (!e.urlAfterRedirects.includes('#')) {
          this.viewportScroller.scrollToPosition([0, 0]);
        }
      } else if (e instanceof NavigationCancel || e instanceof NavigationError) {
        if (!this.firstNavDone) {
          this.firstNavDone = true;
          this.dismissLoadingOverlay();
        }
        this.isNavigating.set(false);
      }
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  private injectGtm(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const host = window.location.hostname;
    if (host === 'localhost' || host === '127.0.0.1') return;

    const GTM_ID = 'GTM-PS4CGQND';

    const script = document.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${GTM_ID}');`;
    document.head.insertBefore(script, document.head.firstChild);

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${GTM_ID}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`;
    document.body.insertBefore(noscript, document.body.firstChild);
  }

  private dismissLoadingOverlay(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const overlay = document.getElementById('app-loading-overlay');
    if (!overlay) return;
    overlay.classList.add('fade-out');
    setTimeout(() => overlay.remove(), 350);
  }
}
